import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Button,
  Typography,
  Paper,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Tooltip,
  Badge
} from '@mui/material';
import { 
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as ApproveIcon,
  Close,
  LocalShipping as TransferIcon,
  FilterList as FilterIcon,
  FileDownload as ExportIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import { getCurrentUser } from '../../services/authService';
import { formatDate, formatTime } from '../../utils/dateFormatter';
import * as XLSX from 'xlsx';
import stockTransferService from '../../services/stockTransferService';

const StockTransferPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const user = getCurrentUser();
  const companyId = user?.companyId || 1;
  
  // State management
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Helper function to safely parse and format dates
  const formatTransferDate = (dateValue) => {
    return formatDate(dateValue);
  };

  // Helper function to format full date with time
  const formatFullDate = (dateValue) => {
    return formatDate(dateValue);
  };

  // Helper function to format time for display
  const formatTransferTime = (dateValue) => {
    return formatTime(dateValue);
  };

  // Note: The backend returns transfers in this format:
  // {
  //   id, locationId, locationName, items[], referenceNo, notes, transferDate, createdBy
  // }

  // Fetch stock transfers
  const fetchTransfers = useCallback(async () => {
    setLoading(true);
    try {
      // Get user's location
      const currentUser = getCurrentUser();
      const userLocation = currentUser?.location || null;
      const isLocationUser = userLocation && userLocation.id;
      
      const response = await stockTransferService.getPaginatedTransfers(
        page,
        rowsPerPage,
        'transactionDate',
        'DESC',
        searchQuery || null
      );
      
      // Filter transfers by user location if not admin (backend already filters, but this is a safety check)
      let filteredTransfers = response.content || [];
      if (isLocationUser && userLocation) {
        const userLocationId = userLocation.id;
        const userLocationName = userLocation.name?.toLowerCase();
        
        // Filter to show only transfers where user's location is involved (from or to)
        filteredTransfers = filteredTransfers.filter(transfer => {
          const fromLocationName = transfer.fromLocationName?.toLowerCase() || '';
          const toLocationName = transfer.locationName?.toLowerCase() || ''; // locationName is the "to" location
          
          // Check if transfer involves user's location
          return fromLocationName === userLocationName || toLocationName === userLocationName ||
                 transfer.fromLocationId === userLocationId || transfer.locationId === userLocationId;
        });
      }
      
      setTransfers(filteredTransfers);
      setTotalElements(filteredTransfers.length);
    } catch (error) {
      console.error('Error fetching transfers:', error);
      enqueueSnackbar('Failed to load stock transfers', { variant: 'error' });
      setTransfers([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, enqueueSnackbar]);

  useEffect(() => {
    fetchTransfers();
  }, [fetchTransfers]);

  // Handle menu open/close
  const handleMenuOpen = (event, transfer) => {
    setAnchorEl(event.currentTarget);
    setSelectedTransfer(transfer);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Handle actions
  const handleView = async (transfer) => {
    try {
      const transferDetails = await stockTransferService.getTransferById(transfer.id);
      setSelectedTransfer(transferDetails);
      setViewDialogOpen(true);
      handleMenuClose();
    } catch (error) {
      console.error('Error fetching transfer details:', error);
      enqueueSnackbar('Failed to load transfer details', { variant: 'error' });
    }
  };

  const handleEdit = (transfer) => {
    // TODO: Navigate to edit page or open edit dialog
    console.log('Edit transfer:', transfer);
    enqueueSnackbar('Edit functionality coming soon', { variant: 'info' });
    handleMenuClose();
  };

  const handleCancel = (transfer) => {
    setSelectedTransfer(transfer);
    setCancelDialogOpen(true);
    handleMenuClose();
  };

  const handleDelete = (transfer) => {
    setSelectedTransfer(transfer);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const confirmCancel = async () => {
    if (!selectedTransfer) return;
    
    setActionLoading(true);
    try {
      await stockTransferService.cancelTransfer(selectedTransfer.id);
      enqueueSnackbar('Transfer cancelled successfully. Stock has been reversed.', { variant: 'success' });
      setCancelDialogOpen(false);
      fetchTransfers(); // Refresh the list
    } catch (error) {
      console.error('Error cancelling transfer:', error);
      const errorMessage = error.response?.data?.message || 'Failed to cancel transfer. Please check if there is sufficient stock to reverse.';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!selectedTransfer) return;
    
    setActionLoading(true);
    try {
      await stockTransferService.deleteTransfer(selectedTransfer.id);
      enqueueSnackbar('Transfer deleted successfully. All transactions have been removed.', { variant: 'success' });
      setDeleteDialogOpen(false);
      fetchTransfers(); // Refresh the list
    } catch (error) {
      console.error('Error deleting transfer:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete transfer. Please check if there is sufficient stock to reverse.';
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  // Handle search
  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle export to Excel
  const handleExport = () => {
    try {
      setIsExporting(true);
      
      const exportData = transfers.map(transfer => ({
        'Transfer Number': transfer.referenceNo || `TR-${transfer.id}`,
        'From Location': transfer.fromLocationName || 'Warehouse',
        'To Location': transfer.locationName || 'Unknown',
        'Transfer Date': formatTransferDate(transfer.transferDate),
        'Total Items': transfer.items ? transfer.items.length : 0,
        'Total Quantity': transfer.items ? transfer.items.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0) : 0,
        'Created By': transfer.createdBy || 'System',
        'Notes': transfer.notes || ''
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Stock Transfers');
      
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      const today = new Date();
      const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      link.download = `stock_transfers_${dateStr}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      enqueueSnackbar('Export successful', { variant: 'success' });
    } catch (error) {
      console.error('Error exporting:', error);
      enqueueSnackbar('Failed to export data', { variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TransferIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              <Box>
                <Typography variant="h4" component="h1" fontWeight="bold">
                  Stock Transfers
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage stock transfers between locations
            </Typography>
              </Box>
            </Box>
            <Button 
              variant="contained"
              startIcon={<AddIcon />}
              size="large"
              onClick={() => navigate('/inventory/stock-transfer/create')}
            >
              New Transfer
            </Button>
          </Box>
        </Box>

        {/* Filters and Actions */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                size="small"
                placeholder="Search by transfer number, location..."
                  value={searchQuery}
                onChange={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                  )
                  }}
                />
              </Grid>
            <Grid item xs={12} md={8} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Tooltip title="Refresh">
                <IconButton onClick={fetchTransfers} color="primary">
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={isExporting ? <CircularProgress size={20} /> : <ExportIcon />}
                onClick={handleExport}
                disabled={isExporting || transfers.length === 0}
              >
                Export
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Table */}
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Transfer #</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>From → To</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Time</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Items</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Quantity</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Created By</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : transfers.length === 0 ? (
                    <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">
                      No stock transfers found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                transfers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((transfer) => (
                    <TableRow 
                      key={transfer.id}
                      hover
                      sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium" color="primary">
                          {transfer.referenceNo || `TR-${transfer.id}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {transfer.fromLocationName || 'Warehouse'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ↓ {transfer.locationName || 'Unknown'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatTransferDate(transfer.transferDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatTransferTime(transfer.transferDate)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={transfer.items ? transfer.items.length : 0}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                          <Typography variant="body2" fontWeight="medium">
                          {transfer.items ? transfer.items.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0) : 0}
                          </Typography>
                        </TableCell>
                      <TableCell align="center">
                        <Chip
                          label="COMPLETED"
                          color="success"
                          size="small"
                          sx={{ fontWeight: 'medium', minWidth: '90px' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {transfer.createdBy || 'System'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, transfer)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
          <TablePagination
            component="div"
            count={totalElements}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
            </TableContainer>

        {/* Actions Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => handleView(selectedTransfer)}>
            <ViewIcon sx={{ mr: 1 }} fontSize="small" />
            View Details
          </MenuItem>
          <MenuItem onClick={() => handleCancel(selectedTransfer)}>
            <Close sx={{ mr: 1 }} fontSize="small" color="warning" />
            Cancel Transfer
          </MenuItem>
          <MenuItem onClick={() => handleDelete(selectedTransfer)} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            Delete Transfer
          </MenuItem>
        </Menu>

        {/* View Dialog */}
        <Dialog
          open={viewDialogOpen}
          onClose={() => setViewDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TransferIcon color="primary" />
              Transfer Details - {selectedTransfer?.referenceNo || `TR-${selectedTransfer?.id}`}
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {selectedTransfer && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    From Location
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedTransfer.fromLocationName || 'Warehouse'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    To Location
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {selectedTransfer.locationName || 'Unknown'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Transfer Date
                  </Typography>
                  <Typography variant="body1">
                    {formatFullDate(selectedTransfer.transferDate)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Status
                  </Typography>
                  <Chip
                    label="COMPLETED"
                    color="success"
                    size="small"
                />
              </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Created By
                  </Typography>
                  <Typography variant="body1">
                    {selectedTransfer.createdBy || 'System'}
                  </Typography>
                </Grid>
                {selectedTransfer.notes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Notes
                    </Typography>
                    <Typography variant="body1">
                      {selectedTransfer.notes}
                    </Typography>
            </Grid>
                )}
                
                {/* Transfer Items */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
                    Transfer Items
          </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Product</TableCell>
                          <TableCell align="right">Quantity</TableCell>
                          <TableCell>Unit</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedTransfer.items.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>{item.productName || 'Unknown Product'}</TableCell>
                            <TableCell align="right">{parseFloat(item.quantity).toFixed(2)}</TableCell>
                            <TableCell>PCS</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Cancel Confirmation Dialog */}
        <Dialog 
          open={cancelDialogOpen} 
          onClose={() => !actionLoading && setCancelDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Close color="warning" />
            Cancel Transfer
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to cancel transfer{' '}
              <strong>{selectedTransfer?.referenceNo || `TR-${selectedTransfer?.id}`}</strong>?
            </Typography>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.lighter', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>What happens when you cancel:</strong>
                <ul style={{ marginTop: 8, marginBottom: 0 }}>
                  <li>Stock will be returned to the original location</li>
                  <li>Reverse transactions will be created</li>
                  <li>This action requires sufficient stock at the destination</li>
                </ul>
              </Typography>
        </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCancelDialogOpen(false)} disabled={actionLoading}>
              No, Keep Transfer
            </Button>
            <Button 
              onClick={confirmCancel} 
              variant="contained" 
              color="warning"
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={20} /> : null}
            >
              {actionLoading ? 'Cancelling...' : 'Yes, Cancel Transfer'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog 
          open={deleteDialogOpen} 
          onClose={() => !actionLoading && setDeleteDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DeleteIcon color="error" />
            Delete Transfer
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to permanently delete transfer{' '}
              <strong>{selectedTransfer?.referenceNo || `TR-${selectedTransfer?.id}`}</strong>?
            </Typography>
            <Box sx={{ mt: 2, p: 2, bgcolor: 'error.lighter', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>⚠️ Warning - This action:</strong>
                <ul style={{ marginTop: 8, marginBottom: 0 }}>
                  <li>Will reverse all inventory movements</li>
                  <li>Will permanently delete all related transactions</li>
                  <li>Cannot be undone</li>
                  <li>Requires sufficient stock at the destination to reverse</li>
                </ul>
              </Typography>
    </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)} disabled={actionLoading}>
              No, Keep Transfer
            </Button>
            <Button 
              onClick={confirmDelete} 
              variant="contained" 
              color="error"
              disabled={actionLoading}
              startIcon={actionLoading ? <CircularProgress size={20} /> : null}
            >
              {actionLoading ? 'Deleting...' : 'Yes, Delete Permanently'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default StockTransferPage;
